import { $core } from '../../../CORE/src/front/$core'
import { toStartDateVersionCode } from './masterVersionCode'

export type YhcMastersFormSaving = {
  id?: string
  version?: string
  enabledFrom?: string
  disabledAt?: string
  versionDesc?: string
  [key: string]: unknown
}

const hasOwn = (obj: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key)

const isBlank = (value: unknown): boolean =>
  value == null || String(value).trim() === ''

/**
 * フォーム payload に期間系フィールドが含まれているか。
 * 含まれない部分更新では versions を触らない。
 */
export const hasPeriodFieldsInPayload = (saving: YhcMastersFormSaving): boolean =>
  hasOwn(saving, 'enabledFrom') ||
  hasOwn(saving, 'disabledAt') ||
  hasOwn(saving, 'versionDesc')

export const validateMasterVersionPeriodFields = (
  saving: YhcMastersFormSaving,
): void => {
  if (isBlank(saving.enabledFrom)) {
    throw new Error('有効期間 開始を入力してください')
  }
  if (isBlank(saving.disabledAt)) {
    throw new Error('有効期間 終了を入力してください')
  }
  if (isBlank(saving.versionDesc)) {
    throw new Error('バージョン説明を入力してください')
  }
  const from = String(saving.enabledFrom).trim()
  const to = String(saving.disabledAt).trim()
  if (from > to) {
    throw new Error('有効期間 開始～終了の日付指定が正しくありません')
  }
}

type FindDeps = {
  findMastersByVersion: (version: string) => Promise<{ id?: string } | null>
  upsertVersion: (row: {
    id: string
    enabledFrom: string
    disabledAt: string
    desc: string
  }) => Promise<unknown>
}

/**
 * yhc_masters 保存前に yhc_master_versions を同期する純ロジック。
 * 新規: コード自動採番 → versions を先に upsert → saving.version をセット
 * 更新: 既存 version を維持。期間が payload にあるときだけ versions を更新
 * 孤立 versions（masters 無し）: 再利用
 */
export const syncYhcMasterVersionFromMastersFormCore = async (
  saving: YhcMastersFormSaving,
  deps: FindDeps,
): Promise<YhcMastersFormSaving> => {
  const existingVersion = String(saving.version || '').trim()
  const periodTouched = hasPeriodFieldsInPayload(saving)

  // 既存版 + 期間なしの部分更新 → versions 非タッチ
  if (existingVersion && !periodTouched) {
    return saving
  }

  if (!existingVersion && !periodTouched) {
    throw new Error('有効期間 開始を入力してください')
  }

  if (periodTouched) {
    validateMasterVersionPeriodFields(saving)
  }

  let version = existingVersion
  if (!version) {
    version = toStartDateVersionCode(String(saving.enabledFrom))
    const collidingMaster = await deps.findMastersByVersion(version)
    if (collidingMaster && collidingMaster.id !== saving.id) {
      throw new Error(
        `バージョン ${version} は既にマスタデータで使われています。同じ開始日の新規登録はできません。既存レコードを更新するか、開始日を変えてください。`,
      )
    }
  }

  // 新規採番、または期間更新時に versions を upsert（孤立 versions は再利用）
  if (!existingVersion || periodTouched) {
    await deps.upsertVersion({
      id: version,
      enabledFrom: String(saving.enabledFrom).trim(),
      disabledAt: String(saving.disabledAt).trim(),
      desc: String(saving.versionDesc).trim(),
    })
  }

  saving.version = version
  return saving
}

type YhcMasterVersionUpsertRow = {
  id: string
  enabledFrom: string
  disabledAt: string
  desc: string
}

/**
 * 版の同期は masters 保存の前段なので、一覧の再取得と保存トーストは masters 側に任せる。
 * versions でも再取得すると find が2本重なり、キャンセル側が isLoading を下ろして
 * v-once の一覧行が更新前の有効期間のまま残る。
 */
export const yhcMasterVersionUpsertArgs = (row: YhcMasterVersionUpsertRow) => ({
  data: row,
  quiet: true,
  disableUserLatestStoreMethodActionTime: true,
})

/**
 * ブラウザの $core.$models を使うラッパー
 */
export const syncYhcMasterVersionFromMastersForm = async (
  saving: YhcMastersFormSaving,
): Promise<YhcMastersFormSaving> => {
  return syncYhcMasterVersionFromMastersFormCore(saving, {
    findMastersByVersion: async (version) => {
      const rows = await $core.$models.yhc_masters.find({
        filter: { version: { _eq: version } },
        fields: ['id', 'version'],
        limit: 1,
      })
      return rows?.[0] || null
    },
    upsertVersion: async (row) => {
      await $core.$models.yhc_master_versions.upsert(yhcMasterVersionUpsertArgs(row))
      try {
        $core.$obCache?.delete?.('$yhcFetchVersions')
      } catch {
        // キャッシュ削除はベストエフォート
      }
    },
  })
}

/**
 * 編集フォーム用: versions の期間・説明を masters レコードへ載せる
 */
export const hydrateYhcMastersWithVersionPeriod = async <
  T extends { version?: string; enabledFrom?: string; disabledAt?: string; versionDesc?: string },
>(
  record: T,
): Promise<T> => {
  if (!record?.version) {
    return record
  }
  const versionRow = await $core.$models.yhc_master_versions.findById(record.version)
  if (!versionRow) {
    return record
  }
  record.enabledFrom = versionRow.enabledFrom
  record.disabledAt = versionRow.disabledAt
  record.versionDesc = versionRow.desc
  return record
}

export const stripYhcMastersVirtualPeriodFields = <T extends Record<string, unknown>>(
  record: T,
): T => {
  delete record.enabledFrom
  delete record.disabledAt
  delete record.versionDesc
  return record
}
